import { SelectOption, TableHeader, TableRowOption } from '@common/types/common';
import { EVENT_TABLE_ROWS_PER_PAGE } from '@event/utils/event';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import {
    Checkbox,
    CircularProgress,
    Collapse,
    IconButton,
    Table as MUITable,
    Menu,
    MenuItem,
    Radio,
    TableBody,
    TableCell,
    TableCellProps,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableRowProps,
    TableSortLabel,
    Tooltip,
    Typography,
    alpha,
    styled,
} from '@mui/material';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getError } from '@utils/common';
import clsx from 'clsx';
import { NestedMenuItem } from 'mui-nested-menu';
import React, { JSX, ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, LinkProps } from 'react-router-dom';

const defaultRowHeight = 53;

type TableSort<H> =
    | {
          column: keyof H;
          direction: 'asc' | 'desc';
      }
    | string;

const normalizeSort = <H,>(sort?: TableSort<H> | null) => {
    if (typeof sort !== 'string') {
        return sort;
    }

    const isDescending = sort.startsWith('-');
    return {
        column: (isDescending ? sort.slice(1) : sort) as keyof H,
        direction: isDescending ? ('desc' as const) : ('asc' as const),
    };
};

const RowOptionTableCell = styled(TableCell)<
    TableCellProps & {
        isAtEndOfScroll: boolean;
        separator: ITableProps<any, any>['separator'];
        index?: number;
    }
>(({ isAtEndOfScroll, separator, index }) => ({
    backgroundColor: separator === 'gray' && !!index && index % 2 !== 0 ? '#FBFBFB' : '#fff',
    minWidth: '118px',
    maxWidth: '118px',
    width: '118px',
    textAlign: 'center',
    position: 'sticky',
    right: 0,
    zIndex: 10,
    ...(separator === 'gray' && Number(index) >= 0 && { borderBottom: 0 }),
    boxShadow: !isAtEndOfScroll
        ? `-6px 0 5px -5px rgba(0,0,0,0.1), 2px 0 0 0 ${
              separator === 'gray' && !!index && index % 2 !== 0 ? '#FBFBFB' : '#fff'
          }`
        : `2px 0 0 0 ${separator === 'gray' && !!index && index % 2 !== 0 ? '#FBFBFB' : '#fff'}`,
    '&::before': !isAtEndOfScroll
        ? {
              content: '""',
              position: 'absolute',
              left: '-30px',
              top: 0,
              bottom: 0,
              width: '30px',
              background:
                  separator === 'gray' && !!index && index % 2 !== 0
                      ? 'linear-gradient(to right, rgba(251, 251, 251, 0) 0%, rgba(251, 251, 251, .6) 50%, rgba(251, 251, 251, .8) 100%)'
                      : 'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, .6) 50%, rgba(255, 255, 255, .8) 100%)',
              pointerEvents: 'none',
              zIndex: 1,
          }
        : {},
}));

export interface ITableProps<T, H> {
    id?: string;
    selectionKey?: keyof T;
    headers: TableHeader<H>[];
    sort?: TableSort<H> | null;
    rootClassname?: string;
    onSortClick?: (column: keyof H) => void;
    data: T[];
    renderRow: (item: T, collapsibleAction?: ReactElement) => ReactElement;
    emptyListTxt?: string;
    emptyListClassName?: string;
    centerEmptyListTxt?: boolean;
    renderKey?: (item: T) => string;
    rowsPerPage?: number;
    rowsPerPageOptions?: SelectOption<number>[];
    setRowsPerPage?: (page: number) => void;
    selectable?: boolean;
    isSelectDisabled?: boolean;
    isSelectAllLoading?: boolean;
    selectableProps?: TableCellProps;
    alignSelectable?: boolean;
    onRowCheckClick?: (row: T) => void;
    isRowSelectable?: (row: T) => boolean;
    className?: string;
    containerClassname?: string;
    getHref?: (row: T) => string | undefined | null;
    hrefProps?: Omit<LinkProps, 'to'> & React.RefAttributes<HTMLAnchorElement>;
    onRowClick?: (row: T) => void;
    onSelectAll?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    selectedItems?: (number | string)[];
    showRowOptions?: (row: T) => boolean;
    rowOptions?: {
        data?: TableRowOption<T>[] | ((item?: T) => JSX.Element | TableRowOption<T>[]);
        title?: string | ReactNode;
        tooltip?: string;
        disabled?: {
            state: boolean;
            reason?: string;
        };
        tableCellProps?: TableCellProps;
        tableCellHeaderProps?: TableCellProps;
    };
    querysetIds?: (number | string)[];
    page?: number;
    onPageChange?: (page: number) => void;
    visibleRows?: T[];
    count?: number;
    isLoading?: boolean;
    bigHeaders?: boolean;
    collapsableContent?: (item: T) => ReactElement;
    bottomContainerProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
    separator?: 'stroke' | 'gray';
    fillMissingRows?: boolean;
    scrollOnPageChange?: boolean;
    pagination?: boolean;
    error?: FetchBaseQueryError | SerializedError;
    counter?: {
        label: string;
    };
    allSelectable?: boolean;
    maxVisibleRows?: number;
    footerPortalRef?: React.RefObject<HTMLElement | null>;
    useRadio?: boolean;
    truncateCells?: boolean;
    /**
     * Use this in a flex box
     */
    fullHeight?: boolean;
    /**
     * If true, the empty list message will have a minimum height to fill the table body area. If false, the empty list message will only take up as much space as it needs.
     */
    emptyMinHeight?: boolean;
    tableRowProps?: Omit<TableRowProps, 'children'> | ((row: T) => Omit<TableRowProps, 'children'>);
}

// Recursively extract a plain string from a ReactNode for use as tooltip title.
const extractString = (node: ReactNode): string | undefined => {
    if (typeof node === 'string') return node || undefined;
    if (typeof node === 'number') return String(node);
    if (React.isValidElement(node)) {
        const el = node as React.ReactElement<{ children?: ReactNode }>;
        return extractString(el.props.children);
    }
    return undefined;
};

// Clones every TableCell in the renderRow output with truncation styles and
// a Tooltip showing the full text when the value can be inferred as a string.
const wrapAllCells = (rendered: ReactElement): ReactNode => {
    const newChildren = React.Children.map(
        (rendered as React.ReactElement<{ children: ReactNode }>).props.children,
        (child: ReactNode) => {
            if (!React.isValidElement(child)) return child;
            const cellEl = child as React.ReactElement<{ children?: ReactNode; sx?: object }>;
            // Only wrap actual TableCells, leave other elements (e.g. collapsible buttons) untouched.
            if (!cellEl.type || (cellEl.type as { displayName?: string; muiName?: string })?.muiName !== 'TableCell')
                return child;

            const tooltipTitle = extractString(cellEl.props.children);

            const cloned = React.cloneElement(cellEl, {
                sx: {
                    ...(cellEl.props.sx ?? {}),
                    maxWidth: 0,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                },
            });

            if (!tooltipTitle) return cloned;

            return (
                <Tooltip title={tooltipTitle} arrow placement="top-start">
                    {cloned}
                </Tooltip>
            );
        }
    );

    return <>{newChildren}</>;
};

const Table = <T, H>({
    id,
    headers,
    selectionKey = 'id' as keyof T,
    data,
    rootClassname,
    rowsPerPage = EVENT_TABLE_ROWS_PER_PAGE,
    rowsPerPageOptions,
    setRowsPerPage,
    renderRow,
    selectable,
    isSelectDisabled,
    isSelectAllLoading,
    selectableProps,
    alignSelectable,
    isRowSelectable,
    className,
    containerClassname,
    renderKey,
    emptyListTxt,
    emptyListClassName,
    centerEmptyListTxt = false,
    selectedItems,
    querysetIds,
    onSelectAll,
    onRowCheckClick,
    onSortClick,
    page,
    onPageChange,
    sort,
    rowOptions: rowOptionsProps,
    visibleRows,
    count,
    isLoading,
    showRowOptions,
    getHref,
    hrefProps,
    onRowClick,
    bottomContainerProps,
    collapsableContent,
    bigHeaders,
    separator = 'stroke',
    fillMissingRows = true,
    scrollOnPageChange,
    pagination = true,
    error,
    counter,
    allSelectable = true,
    maxVisibleRows,
    footerPortalRef,
    useRadio = false,
    truncateCells = false,
    fullHeight = false,
    emptyMinHeight = true,
    tableRowProps,
}: ITableProps<T, H>) => {
    const normalizedSort = normalizeSort(sort);
    const {
        data: rowOptions,
        title: rowOptionsTitle,
        tooltip: rowOptionsTooltip,
        disabled,
        tableCellProps,
        tableCellHeaderProps,
    } = rowOptionsProps || {};
    const containerRef = useRef<HTMLDivElement>(null);
    const tableBodyRef = useRef<HTMLTableSectionElement>(null);
    const tableHeadRef = useRef<HTMLTableSectionElement>(null);
    const [anchor, setAnchor] = useState<null | {
        el: HTMLElement;
        data: T;
        menuOptions?: TableRowOption<T>[];
    }>(null);
    const [menuOpen, setMenuOpen] = useState(false);

    const [isAtEndOfScroll, setIsAtEndOfScroll] = useState(false);
    const [tableHeadHeight, setTableHeadHeight] = useState(0);
    const rowsElement = tableBodyRef.current?.getElementsByClassName('MuiTableRow-root');

    const rows = useMemo(() => visibleRows ?? data, [data, visibleRows]);
    const hasLinkColumn = useMemo(() => !!getHref && rows.some((row) => !!getHref(row)), [getHref, rows]);

    const fillTargetRows = useMemo(() => {
        if (maxVisibleRows && maxVisibleRows > 0) {
            return Math.min(rowsPerPage, maxVisibleRows);
        }

        return rowsPerPage;
    }, [maxVisibleRows, rowsPerPage]);

    const emptyRows = useMemo(
        () => (Number(page) >= 1 ? Math.max(fillTargetRows - rows.length, 0) : 0),
        [fillTargetRows, page, rows.length]
    );

    const rowHeight = useMemo(
        () => (rowsElement?.length && rowsElement[0].clientHeight ? rowsElement[0].clientHeight : defaultRowHeight),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rowsElement?.[0]?.clientHeight]
    ); // We want to listen to the first rows' height change, so the rest of the table will be filled with this height * number of missing rows

    const tableBodyMaxHeight = useMemo(() => {
        if (!maxVisibleRows || maxVisibleRows <= 0) {
            return undefined;
        }

        return rowHeight * maxVisibleRows;
    }, [maxVisibleRows, rowHeight]);

    const tableContainerMaxHeight = useMemo(() => {
        if (!tableBodyMaxHeight) {
            return undefined;
        }

        return tableBodyMaxHeight + tableHeadHeight;
    }, [tableBodyMaxHeight, tableHeadHeight]);

    const handleScroll = () => {
        if (containerRef.current) {
            const isScrolledToEnd =
                containerRef.current.scrollWidth - containerRef.current.scrollLeft === containerRef.current.clientWidth;
            setIsAtEndOfScroll(isScrolledToEnd);
        }
    };

    const isFunctionalRowOptions = typeof rowOptions === 'function';
    const menuHasItems = useMemo(() => {
        const anchorMenuItems = anchor?.el && anchor?.menuOptions && anchor.menuOptions.length > 0;
        const rowOptionsItems = rowOptions && rowOptions.length > 0;
        return isFunctionalRowOptions ? anchorMenuItems : rowOptionsItems;
    }, [rowOptions, anchor, isFunctionalRowOptions]);

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            handleScroll(); // Set initial shadow state
        }

        if (rowOptions?.length || rowOptions) {
            window.addEventListener('resize', handleScroll);
        }
        return () => {
            container?.removeEventListener('scroll', handleScroll);
            if (rowOptions?.length || rowOptions) {
                window.removeEventListener('resize', handleScroll);
            }
        };
    }, [rowOptions, rowOptions?.length]);

    useEffect(() => {
        const tableHeadElement = tableHeadRef.current;
        if (!tableHeadElement) {
            return;
        }

        const updateTableHeadHeight = () => {
            setTableHeadHeight(tableHeadElement.clientHeight || 0);
        };

        updateTableHeadHeight();

        const resizeObserver = new ResizeObserver(updateTableHeadHeight);
        resizeObserver.observe(tableHeadElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [headers.length, rowOptionsTitle, rowOptionsTooltip, selectable, count, bigHeaders, getHref]);

    const handleCloseMenu = useCallback(() => {
        setMenuOpen(false);
        setAnchor(null);
    }, []);

    const Row = ({ row, index }: { row: T; index: number }) => {
        const [open, setOpen] = useState(false);
        const rowHref = getHref?.(row);
        const hasRowHref = !!rowHref;
        // Handler for radio/checkbox click
        const handleSelect = (event: React.MouseEvent) => {
            event.stopPropagation();
            if (onRowCheckClick) {
                if (useRadio) {
                    // Only call if not already selected
                    if (selectedItems?.[0] !== row[selectionKey]) {
                        onRowCheckClick(row);
                    }
                } else {
                    onRowCheckClick(row);
                }
            }
        };
        return (
            <>
                <TableRow
                    id={row[selectionKey]?.toString()}
                    key={renderKey ? renderKey(row) : String(row[selectionKey])}
                    {...(typeof tableRowProps === 'function' ? tableRowProps(row) : tableRowProps)}
                    className={clsx(
                        'group',
                        {
                            'hover:cursor-default': !hasRowHref && !onRowClick,
                            'cursor-pointer': !!onRowClick,
                            'hover:bg-little-boy-blue-10': hasRowHref || !!onRowClick,
                            'bg-neutral-700': open,
                            'bg-white': separator === 'gray' && index % 2 === 0,
                            'bg-[#FBFBFB]': separator === 'gray' && index % 2 !== 0,
                        },
                        typeof tableRowProps === 'function' ? tableRowProps(row)?.className : tableRowProps?.className
                    )}
                    sx={{
                        '& > *': {
                            borderBottom: (theme) =>
                                separator === 'stroke'
                                    ? !collapsableContent
                                        ? `1px solid ${theme.palette['neutral-900'].main}12`
                                        : !!collapsableContent && !open
                                        ? 'unset'
                                        : `1px solid ${theme.palette['neutral-900'].main}12`
                                    : 'none !important',
                            color: (theme) => theme.palette['neutral-900'].main,
                        },
                        transform: 'translate(0)',
                        clipPath: 'inset(0)',
                        ...(typeof tableRowProps === 'function' ? tableRowProps(row)?.sx : tableRowProps?.sx),
                    }}
                    onClick={(e) => {
                        onRowClick?.(row);
                        typeof tableRowProps === 'function'
                            ? tableRowProps(row)?.onClick?.(e)
                            : tableRowProps?.onClick?.(e);
                    }}
                >
                    {hasLinkColumn ? (
                        <td>
                            {hasRowHref ? (
                                <a
                                    href={rowHref ?? '#'}
                                    className="absolute top-0 left-0 w-full h-full z-10"
                                    {...hrefProps}
                                ></a>
                            ) : null}
                        </td>
                    ) : null}
                    {selectable && ((isRowSelectable && isRowSelectable(row)) || !isRowSelectable) ? (
                        <TableCell
                            padding="checkbox"
                            className={hasRowHref ? 'relative z-20' : undefined}
                            sx={{
                                ...(separator === 'gray' && { borderBottom: 0 }),
                            }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {useRadio ? (
                                <Radio
                                    color="primary"
                                    disabled={isSelectDisabled || isSelectAllLoading}
                                    checked={selectedItems?.[0] === row[selectionKey]}
                                    onClick={handleSelect}
                                />
                            ) : (
                                <Checkbox
                                    color="primary"
                                    disabled={isSelectDisabled || isSelectAllLoading}
                                    checked={selectedItems?.includes(row[selectionKey] as number | string)}
                                    onClick={handleSelect}
                                />
                            )}
                        </TableCell>
                    ) : selectable ? (
                        <TableCell
                            sx={{
                                ...(separator === 'gray' && { borderBottom: 0 }),
                            }}
                        />
                    ) : null}
                    {truncateCells
                        ? wrapAllCells(
                              renderRow(
                                  row,
                                  collapsableContent ? (
                                      <IconButton
                                          aria-label="expand row"
                                          size="small"
                                          onClick={() => setOpen(!open)}
                                          color="primary"
                                          className="w-[17px] h-[17px] md:w-auto md:h-auto"
                                          sx={{
                                              transition: 'rotate 0.1s',
                                              rotate: open ? '90deg' : '',
                                          }}
                                      >
                                          <KeyboardArrowRightOutlinedIcon className="w-[17px] h-[17px] md:w-[24px] md:h-[24px]" />
                                      </IconButton>
                                  ) : undefined
                              )
                          )
                        : renderRow(
                              row,
                              collapsableContent ? (
                                  <IconButton
                                      aria-label="expand row"
                                      size="small"
                                      onClick={() => setOpen(!open)}
                                      color="primary"
                                      className="w-[17px] h-[17px] md:w-auto md:h-auto"
                                      sx={{
                                          transition: 'rotate 0.1s',
                                          rotate: open ? '90deg' : '',
                                      }}
                                  >
                                      <KeyboardArrowRightOutlinedIcon className="w-[17px] h-[17px] md:w-[24px] md:h-[24px]" />
                                  </IconButton>
                              ) : undefined
                          )}
                    {(Array.isArray(rowOptions) &&
                        rowOptions?.length &&
                        ((showRowOptions && showRowOptions(row)) || !showRowOptions)) ||
                    isFunctionalRowOptions ? (
                        <RowOptionTableCell
                            padding="checkbox"
                            className={clsx('sticky right-0', {
                                'group-hover:!bg-transparent': !!getHref || !!onRowClick,
                            })}
                            isAtEndOfScroll={isAtEndOfScroll}
                            separator={separator}
                            index={index}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {!isFunctionalRowOptions ? (
                                <Tooltip title={rowOptionsProps?.disabled?.reason} arrow>
                                    <span>
                                        <IconButton
                                            onClick={(e) => {
                                                e.preventDefault(); // In case of link rows, avoid redirecting.
                                                if (Array.isArray(rowOptions) && rowOptions.length > 0) {
                                                    setAnchor({
                                                        el: e.currentTarget,
                                                        data: row,
                                                        menuOptions: rowOptions,
                                                    });
                                                    setTimeout(() => setMenuOpen(true), 100);
                                                }
                                            }}
                                            disabled={rowOptionsProps?.disabled?.state}
                                        >
                                            <MoreVertIcon color="action" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                (() => {
                                    const result = rowOptions(row);
                                    if (Array.isArray(result)) {
                                        return (
                                            <Tooltip title={rowOptionsProps?.disabled?.reason} arrow>
                                                <span>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setAnchor({
                                                                el: e.currentTarget,
                                                                data: row,
                                                                menuOptions: result,
                                                            });
                                                            setTimeout(() => setMenuOpen(true), 100);
                                                        }}
                                                        disabled={rowOptionsProps?.disabled?.state}
                                                    >
                                                        <MoreVertIcon color="action" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        );
                                    }
                                    return result;
                                })()
                            )}
                        </RowOptionTableCell>
                    ) : Array.isArray(rowOptions) && rowOptions?.length ? (
                        <TableCell
                            sx={{
                                ...(separator === 'gray' && { borderBottom: 0 }),
                            }}
                        />
                    ) : null}
                </TableRow>
                {collapsableContent ? (
                    <TableRow
                        className="bg-neutral-700"
                        sx={{
                            '& > *': {
                                borderBottom: open
                                    ? (theme) => `1px solid ${theme.palette['neutral-900'].main}12`
                                    : 'unset',
                            },
                        }}
                    >
                        <TableCell
                            className="!py-0"
                            colSpan={
                                headers.length +
                                (selectable ? 1 : 0) +
                                (rowOptions?.length || isFunctionalRowOptions ? 1 : 0)
                            }
                        >
                            <Collapse in={open} timeout="auto" unmountOnExit>
                                {collapsableContent(row)}
                            </Collapse>
                        </TableCell>
                    </TableRow>
                ) : null}
            </>
        );
    };

    const getMenuItem = (option: TableRowOption<T>) =>
        option.items ? (
            <NestedMenuItem
                key={'row-option-' + option.label}
                disabled={option.disabled && anchor?.data ? option.disabled(anchor?.data) : false}
                className={clsx('flex gap-2 justify-between items-center px-4 py-0 [&>*]:min-w-full', option.className)}
                parentMenuOpen
                label={option.label}
                renderLabel={() => (
                    <div className="flex gap-2 border-b-[1px] w-full h-full py-[6px]">
                        {option.isLoading ? <CircularProgress size={20} /> : null}
                        <Typography>{option.label}</Typography>
                        <ChevronRightIcon className="ml-auto" />
                    </div>
                )}
            >
                {option.items.map(getMenuItem)}
            </NestedMenuItem>
        ) : (
            <MenuItem
                key={'bulk-' + option.label}
                disabled={(option.disabled && anchor?.data ? option.disabled(anchor?.data) : false) || option.isLoading}
                className={clsx('px-4 py-0', option.className)}
                onClick={() =>
                    anchor?.data
                        ? (() => {
                              option.onClick?.(anchor?.data);
                              setAnchor(null);
                          })()
                        : undefined
                }
            >
                <div className="flex gap-2 border-b-[1px] w-full h-full py-[6px]">
                    {option.isLoading ? <CircularProgress size={20} /> : null}
                    <Typography>{option.label}</Typography>
                </div>
            </MenuItem>
        );

    const footer = useMemo(() => {
        const footerContent = (
            <div
                {...bottomContainerProps}
                className={clsx(
                    'flex md:items-center flex-col sm:flex-row sm:gap-0 gap-3',
                    {
                        'md:justify-between': !!counter,
                    },
                    bottomContainerProps?.className
                )}
            >
                {counter ? (
                    <Typography
                        fontFamily="Roboto"
                        fontSize={12}
                        sx={{ color: (theme) => alpha(theme.palette.black.main, 0.6) }}
                    >
                        <Typography fontFamily="Roboto" fontSize={12} color="primary" fontWeight={700} component="span">
                            {selectedItems?.length}
                        </Typography>{' '}
                        {counter.label}
                    </Typography>
                ) : null}
                {pagination && count && page && onPageChange ? (
                    <div className="ml-auto flex gap-3 flex-col sm:flex-row sm:gap-0 items-center">
                        <TablePagination
                            component="div"
                            count={count}
                            onPageChange={(_e, value) => {
                                onPageChange(value + 1);
                                if (scrollOnPageChange) {
                                    setTimeout(() => {
                                        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
                                        containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                                    }, 100);
                                }
                            }}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                onPageChange(1);
                                setRowsPerPage?.(Number(e.target.value));
                            }}
                            rowsPerPageOptions={rowsPerPageOptions}
                            page={page - 1}
                            classes={{
                                selectLabel: 'font-roboto text-[12px] tracking-[0.4px] text-black/60',
                                select: 'font-roboto text-[12px] tracking-[0.4px] text-black/90 py-0 pl-0',
                                input: 'ml-1 mr-5',
                                toolbar: 'min-h-0',
                                displayedRows: 'font-roboto text-[12px] tracking-[0.4px] text-black/90',
                            }}
                            slotProps={{
                                select: {
                                    IconComponent: ArrowDropDownIcon,
                                },
                            }}
                        />
                    </div>
                ) : null}
                {anchor?.el && menuHasItems ? (
                    <Menu
                        anchorEl={anchor.el}
                        open={menuOpen}
                        onClose={handleCloseMenu}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        classes={{
                            paper: 'rounded-lg',
                            list: 'py-0',
                        }}
                    >
                        {isFunctionalRowOptions
                            ? (anchor.menuOptions as TableRowOption<T>[]).map(getMenuItem)
                            : (rowOptions as TableRowOption<T>[])?.map(getMenuItem)}
                    </Menu>
                ) : null}
            </div>
        );

        return footerPortalRef?.current ? createPortal(footerContent, footerPortalRef.current) : footerContent;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        anchor,
        bottomContainerProps,
        count,
        counter,
        footerPortalRef,
        getMenuItem,
        onPageChange,
        page,
        pagination,
        rowOptions,
        rowsPerPage,
        rowsPerPageOptions,
        scrollOnPageChange,
        selectedItems,
        setRowsPerPage,
    ]);

    return (
        <div
            className={clsx('relative w-full', rootClassname, {
                'flex flex-col flex-grow min-h-0': centerEmptyListTxt || fullHeight,
            })}
            id={id}
            style={centerEmptyListTxt && emptyMinHeight ? { minHeight: `${rowHeight * rowsPerPage}px` } : undefined}
        >
            <div
                className={clsx({
                    'blur-sm': isLoading,
                    'flex-grow flex flex-col min-h-0': centerEmptyListTxt || fullHeight,
                })}
            >
                <TableContainer
                    ref={containerRef}
                    className={clsx(containerClassname, {
                        'blur-sm': isLoading,
                        'flex-grow flex flex-col min-h-0': centerEmptyListTxt || fullHeight,
                    })}
                    sx={{
                        ...(tableContainerMaxHeight
                            ? {
                                  maxHeight: `${tableContainerMaxHeight}px`,
                              }
                            : {}),
                        ...(fullHeight
                            ? {
                                  overflowY: 'auto',
                                  minHeight: 0,
                              }
                            : {}),
                    }}
                >
                    <MUITable
                        size="medium"
                        className={clsx(className, { 'blur-sm': !!error })}
                        style={truncateCells ? { tableLayout: 'fixed' } : undefined}
                        stickyHeader
                        sx={{
                            '& .MuiTableHead-root .MuiTableCell-root[class*="RowOptionTableCell"]': {
                                zIndex: 11,
                                backgroundColor: '#fff',
                            },
                        }}
                    >
                        <TableHead ref={tableHeadRef}>
                            <TableRow className="h-5">
                                {hasLinkColumn ? <th className="w-0"></th> : null}
                                {selectable && count ? (
                                    allSelectable && !useRadio ? (
                                        <TableCell padding="checkbox">
                                            {isSelectAllLoading ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <Checkbox
                                                    color="primary"
                                                    onChange={onSelectAll}
                                                    disabled={isSelectDisabled}
                                                    checked={
                                                        querysetIds && selectedItems?.length
                                                            ? querysetIds?.every((x) => selectedItems?.includes(x))
                                                            : selectedItems?.length === count && count > 0
                                                    }
                                                />
                                            )}
                                        </TableCell>
                                    ) : (
                                        <TableCell padding="checkbox" />
                                    )
                                ) : null}
                                {headers.map((header) => {
                                    const { sortable, ...headerProps } = header;
                                    return (
                                        <TableCell
                                            key={`table-head-${header.id as string}`}
                                            sortDirection={
                                                sortable === false
                                                    ? undefined
                                                    : normalizedSort?.column === header.id
                                                    ? normalizedSort.direction
                                                    : 'asc'
                                            }
                                            {...headerProps}
                                            id={header.id.toString()}
                                        >
                                            {sortable !== false ? (
                                                <TableSortLabel
                                                    active={onSortClick && normalizedSort?.column === header.id}
                                                    direction={
                                                        onSortClick && normalizedSort?.column === header.id
                                                            ? normalizedSort.direction
                                                            : 'asc'
                                                    }
                                                    onClick={() => onSortClick && onSortClick(header.id)}
                                                    IconComponent={onSortClick && SlideshowOutlinedIcon}
                                                    sx={(theme) => ({
                                                        '& .MuiTableSortLabel-icon': {
                                                            rotate: '90deg',
                                                            color: `${theme.palette.primary.main} !important`,
                                                        },
                                                    })}
                                                    disabled={header.disabled}
                                                >
                                                    <Typography
                                                        className="leading-[unset]"
                                                        variant={bigHeaders ? 'h5' : 'body2'}
                                                        fontWeight={700}
                                                        color={'neutral-900'}
                                                        {...header.labelProps}
                                                    >
                                                        {header.label}
                                                    </Typography>
                                                </TableSortLabel>
                                            ) : (
                                                <Typography
                                                    className="leading-[unset]"
                                                    variant={bigHeaders ? 'h5' : 'body2'}
                                                    fontWeight={700}
                                                    color={'neutral-900'}
                                                    {...header.labelProps}
                                                >
                                                    {header.label}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    );
                                })}
                                {rowOptions?.length || isFunctionalRowOptions ? (
                                    <RowOptionTableCell
                                        isAtEndOfScroll={isAtEndOfScroll}
                                        separator={separator}
                                        {...tableCellHeaderProps}
                                        className={clsx('sticky right-0', tableCellHeaderProps?.className)}
                                    >
                                        {rowOptionsTitle ? (
                                            <Typography
                                                className="leading-[unset] items-center flex gap-1"
                                                variant={bigHeaders ? 'h5' : 'body2'}
                                                fontWeight={700}
                                                color={'neutral-900'}
                                            >
                                                {rowOptionsTooltip ? (
                                                    <Tooltip
                                                        arrow
                                                        title={rowOptionsTooltip}
                                                        placement="top-start"
                                                        slotProps={{
                                                            popper: {
                                                                className: 'max-w-[167px]',
                                                            },
                                                        }}
                                                    >
                                                        <InfoOutlinedIcon color="primary-300" />
                                                    </Tooltip>
                                                ) : null}
                                                {rowOptionsTitle}
                                            </Typography>
                                        ) : null}
                                    </RowOptionTableCell>
                                ) : null}
                            </TableRow>
                        </TableHead>
                        <TableBody ref={tableBodyRef}>
                            {collapsableContent
                                ? rows.map((row, index) => (
                                      <Row key={String(row[selectionKey])} row={row} index={index} />
                                  ))
                                : rows.map((row, index) => {
                                      let selectableHeight;
                                      const rowHref = getHref?.(row);
                                      const hasRowHref = !!rowHref;
                                      if (selectable && alignSelectable) {
                                          const rowElement =
                                              tableBodyRef.current?.getElementsByClassName('MuiTableRow-root')[index];
                                          selectableHeight =
                                              rowElement?.getElementsByClassName('MuiTableCell-root')[1]
                                                  ?.firstElementChild?.clientHeight;
                                      }

                                      return (
                                          <TableRow
                                              id={row[selectionKey]?.toString()}
                                              key={renderKey ? renderKey(row) : String(row[selectionKey])}
                                              {...(typeof tableRowProps === 'function'
                                                  ? tableRowProps(row)
                                                  : tableRowProps)}
                                              className={clsx(
                                                  'relative group',
                                                  {
                                                      'hover:cursor-default': !hasRowHref && !onRowClick,
                                                      'cursor-pointer': !!onRowClick,
                                                      'hover:bg-little-boy-blue-10': hasRowHref || !!onRowClick,
                                                      'bg-white': separator === 'gray' && index % 2 === 0,
                                                      'bg-[#FBFBFB]': separator === 'gray' && index % 2 !== 0,
                                                  },
                                                  typeof tableRowProps === 'function'
                                                      ? tableRowProps(row)?.className
                                                      : tableRowProps?.className
                                              )}
                                              onClick={(e) => {
                                                  onRowClick?.(row);
                                                  typeof tableRowProps === 'function'
                                                      ? tableRowProps(row)?.onClick?.(e)
                                                      : tableRowProps?.onClick?.(e);
                                              }}
                                              sx={{
                                                  transform: 'translate(0)',
                                                  clipPath: 'inset(0)',
                                                  '& > *': {
                                                      borderBottom:
                                                          separator === 'gray'
                                                              ? (theme) =>
                                                                    `1px solid ${theme.palette['neutral-750'].main}`
                                                              : undefined,
                                                  },
                                                  ...(typeof tableRowProps === 'function'
                                                      ? tableRowProps(row)?.sx
                                                      : tableRowProps?.sx),
                                              }}
                                          >
                                              {hasLinkColumn ? (
                                                  <td>
                                                      {hasRowHref ? (
                                                          <Link
                                                              to={rowHref ?? '#'}
                                                              className="absolute top-0 left-0 w-full h-full z-10"
                                                              {...hrefProps}
                                                          />
                                                      ) : null}
                                                  </td>
                                              ) : null}
                                              {selectable &&
                                              ((isRowSelectable && isRowSelectable(row)) || !isRowSelectable) ? (
                                                  <TableCell
                                                      padding="checkbox"
                                                      sx={{
                                                          ...(separator === 'gray' && { borderBottom: 0 }),
                                                      }}
                                                      {...selectableProps}
                                                      className={clsx(selectableProps?.className, {
                                                          'py-[16px]': alignSelectable,
                                                          'relative z-20': hasRowHref,
                                                      })}
                                                      onClick={(event) => {
                                                          event.stopPropagation();
                                                          selectableProps?.onClick?.(event);
                                                      }}
                                                  >
                                                      <div
                                                          style={{
                                                              height: selectableHeight || 'auto',
                                                          }}
                                                      >
                                                          {useRadio ? (
                                                              <Radio
                                                                  color="primary"
                                                                  disabled={isSelectDisabled || isSelectAllLoading}
                                                                  checked={selectedItems?.[0] === row[selectionKey]}
                                                                  onClick={(event) => {
                                                                      event.stopPropagation();
                                                                      onRowCheckClick?.(row);
                                                                  }}
                                                              />
                                                          ) : (
                                                              <Checkbox
                                                                  color="primary"
                                                                  disabled={isSelectDisabled || isSelectAllLoading}
                                                                  className="-my-[9px]"
                                                                  checked={selectedItems?.includes(
                                                                      row[selectionKey] as number | string
                                                                  )}
                                                                  onClick={(event) => {
                                                                      event.stopPropagation();
                                                                      onRowCheckClick?.(row);
                                                                  }}
                                                              />
                                                          )}
                                                      </div>
                                                  </TableCell>
                                              ) : selectable ? (
                                                  <TableCell
                                                      sx={{
                                                          ...(separator === 'gray' && { borderBottom: 0 }),
                                                      }}
                                                  />
                                              ) : null}
                                              {truncateCells ? wrapAllCells(renderRow(row)) : renderRow(row)}
                                              {(rowOptions?.length &&
                                                  ((showRowOptions && showRowOptions(row)) || !showRowOptions)) ||
                                              isFunctionalRowOptions ? (
                                                  <RowOptionTableCell
                                                      padding="checkbox"
                                                      className={clsx('sticky right-0', {
                                                          'group-hover:!bg-transparent': !!getHref || !!onRowClick,
                                                      })}
                                                      scope="row"
                                                      isAtEndOfScroll={isAtEndOfScroll}
                                                      separator={separator}
                                                      index={index}
                                                      {...tableCellProps}
                                                      onClick={(e) => e.stopPropagation()}
                                                  >
                                                      {!isFunctionalRowOptions ? (
                                                          <Tooltip title={disabled?.reason} arrow>
                                                              <span>
                                                                  <IconButton
                                                                      onClick={(e) => {
                                                                          e.preventDefault(); // In case of link rows, avoid redirecting.
                                                                          if (
                                                                              Array.isArray(rowOptions) &&
                                                                              rowOptions.length > 0
                                                                          ) {
                                                                              setAnchor({
                                                                                  el: e.currentTarget,
                                                                                  data: row,
                                                                                  menuOptions: rowOptions,
                                                                              });
                                                                              setTimeout(() => setMenuOpen(true), 100);
                                                                          }
                                                                      }}
                                                                      disabled={disabled?.state}
                                                                  >
                                                                      <MoreVertIcon color="action" />
                                                                  </IconButton>
                                                              </span>
                                                          </Tooltip>
                                                      ) : (
                                                          (() => {
                                                              const result = rowOptions(row);
                                                              if (Array.isArray(result)) {
                                                                  return (
                                                                      <Tooltip title={disabled?.reason} arrow>
                                                                          <span>
                                                                              <IconButton
                                                                                  onClick={(e) => {
                                                                                      e.preventDefault();
                                                                                      setAnchor({
                                                                                          el: e.currentTarget,
                                                                                          data: row,
                                                                                          menuOptions: result,
                                                                                      });
                                                                                      setTimeout(
                                                                                          () => setMenuOpen(true),
                                                                                          100
                                                                                      );
                                                                                  }}
                                                                                  disabled={disabled?.state}
                                                                              >
                                                                                  <MoreVertIcon color="action" />
                                                                              </IconButton>
                                                                          </span>
                                                                      </Tooltip>
                                                                  );
                                                              }
                                                              return result;
                                                          })()
                                                      )}
                                                  </RowOptionTableCell>
                                              ) : rowOptions?.length ? (
                                                  <TableCell />
                                              ) : null}
                                          </TableRow>
                                      );
                                  })}
                            {fillMissingRows && rows.length > 0 && emptyRows > 0 ? (
                                <TableRow
                                    style={{
                                        height: `${rowHeight * (emptyRows > 0 ? emptyRows : 0)}px`,
                                    }}
                                >
                                    <TableCell
                                        colSpan={
                                            headers.length +
                                            (selectable ? 1 : 0) +
                                            (rowOptions?.length || isFunctionalRowOptions ? 1 : 0)
                                        }
                                        className="!border-b-0"
                                    />
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </MUITable>
                    {rows.length === 0 ? (
                        <div
                            className={clsx(
                                'w-full flex items-center justify-center',
                                { 'py-5': !fillMissingRows && !centerEmptyListTxt },
                                emptyListClassName,
                                { 'flex-grow': centerEmptyListTxt }
                            )}
                            style={{
                                minHeight:
                                    fillMissingRows && !centerEmptyListTxt
                                        ? `${(rowHeight ?? defaultRowHeight) * rowsPerPage}px`
                                        : 0,
                            }}
                        >
                            {!error ? (
                                <p className="bg-light-silver-60 py-5 px-8 rounded-xl font-roboto font-semibold text-black-80">
                                    {emptyListTxt}
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                    {error &&
                        (() => {
                            const errorElement = getError(error);
                            return (
                                <div className="absolute top-0 left-0 w-full h-full z-10 flex justify-center items-center">
                                    <div className="bg-light-silver-60 py-5 px-8 rounded-xl font-roboto font-semibold text-black-80">
                                        <p>An error occured:</p>
                                        <br />
                                        {Array.isArray(errorElement)
                                            ? errorElement.map((element) => element)
                                            : errorElement}
                                    </div>
                                </div>
                            );
                        })()}
                </TableContainer>
                {footer}
                {isLoading && (
                    <div className="absolute top-0 left-0 w-full h-full z-10 flex justify-center items-center">
                        <CircularProgress />
                    </div>
                )}
            </div>
        </div>
    );
};

export { Table };
